/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './EndIcon';
import { toolTipProps } from 'componentregistry/utils/commonProps';

const EndIconManager: ComponentManagerFn<'EndIcon'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'EndIcon'>(platform, 'EndIcon'),
    type: 'EndIcon',
    name: 'End Icon',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'Popover'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      icon: {
        label: 'Icon',
        type: 'iconselect',
        helpText: 'Default Icon to be used when the attribute value is null',
      },
      color: {
        label: 'Color',
        type: 'ColorExpression',
        placeholder: 'e.g. primary or #444',
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.icon?.iconName ?? 'Choose icon!',
    category: 'Forms',
    icon: { iconName: 'icons' },
    description: `**Overview**<br/>
The End Icon component is a decorative and functional element designed to be placed at the trailing edge (right side) of input fields. It provides visual cues or actionable triggers related to the input field it adorns.
Use this component to add functionality like "Clear Text," "Search," or "Show Password" buttons inside text boxes, or simply to add visual context (like a currency symbol or validity checkmark).

**1. Configuration & Styling**<br/>
You can customize the appearance of the icon to match your UI requirements.
* **Icon:** Select the specific visual icon to display (e.g., "close", "search", "eye"). Note: This also serves as the default icon if any bound data is null.
* **Color:** Defines the color of the icon. (Type: **ColorExpression**). Usage: You can use theme variables (e.g., "primary", "error") or standard hex codes (e.g., "#444").
* **Tooltip:** Supports standard tooltip properties (via **toolTipProps**), allowing you to show helper text when the user hovers over the icon.

**2. Interactivity**<br/>
The End Icon is not just a static image; it is an actionable zone within the input field.
* **Triggers:** Supports the **TriggerOnClick** child component. This allows you to define specific logic that executes when the icon is clicked (e.g., clearing the parent input field's value).
* **Popover:** Supports the **Popover** child component. Clicking the icon can launch a small overlay menu or info box.

**3. Best Use Cases**<br/>
* **Password Fields:** Adding an "Eye" icon to toggle password visibility.
* **Search Bars:** Adding a magnifying glass icon that triggers the search query when clicked.
* **Clearable Inputs:** Adding a "X" (close) icon that clears the current text in the field.
* **Contextual Help:** Adding a "Question Mark" icon that opens a Popover with detailed instructions for the field.
* **Status Indicators:** Displaying a green checkmark or red warning icon to reflect the validation state of the input.`,
  };
};

export default EndIconManager;
